/**
 * Driver DSL Config — shared configuration objects for driver workflow pages.
 *
 * All business rules, status flows, labels, and colors are defined here
 * as declarative config. No hardcoded logic in page components.
 */

import type { StatusActionDSL, CardDSL } from '@/lib/use-view-template';

// ─── Status color mapping ────────────────────────────────────────────────────

export const TRIP_STATUS_COLORS: Record<string, string> = {
  ASSIGNED:           'bg-blue-100 text-blue-700',
  DISPATCHED:         'bg-indigo-100 text-indigo-700',
  EN_ROUTE_PICKUP:    'bg-amber-100 text-amber-700',
  AT_PICKUP:          'bg-orange-100 text-orange-700',
  LOADING:            'bg-orange-100 text-orange-700',
  EN_ROUTE_DELIVERY:  'bg-purple-100 text-purple-700',
  AT_DELIVERY:        'bg-violet-100 text-violet-700',
  UNLOADING:          'bg-violet-100 text-violet-700',
  COMPLETED:          'bg-green-100 text-green-700',
  CANCELLED:          'bg-gray-100 text-gray-500',
  IN_PROGRESS:        'bg-amber-100 text-amber-700',
};

// ─── Trip type labels ────────────────────────────────────────────────────────

export const TRIP_TYPE_LABELS: Record<string, string> = {
  FCL_IMP:       'FCL Import',
  FCL_EXP:       'FCL Export',
  FCL_RET:       'FCL Return',
  FCL_COL:       'FCL Collection',
  LCL_PICKUP:    'LCL Pickup',
  LCL_DELIVERY:  'LCL Delivery',
  LCL_TRANSFER:  'LCL Transfer',
};

// ─── Status grouping ─────────────────────────────────────────────────────────

export const ACTIVE_STATUSES = new Set([
  'EN_ROUTE_PICKUP', 'AT_PICKUP', 'LOADING',
  'EN_ROUTE_DELIVERY', 'AT_DELIVERY', 'UNLOADING',
  'IN_PROGRESS',
]);

export const COMPLETED_STATUSES = new Set(['COMPLETED', 'CANCELLED']);

export type TripGroup = 'active' | 'upcoming' | 'completed';

export function groupTrip(status: string): TripGroup {
  if (ACTIVE_STATUSES.has(status)) return 'active';
  if (COMPLETED_STATUSES.has(status)) return 'completed';
  return 'upcoming';
}

// ─── Status flow (DSL-driven) ────────────────────────────────────────────────

export const STATUS_ACTIONS: StatusActionDSL[] = [
  { status: 'ASSIGNED',          nextStatus: 'EN_ROUTE_PICKUP',   label: 'Start Trip',             icon: 'play' },
  { status: 'DISPATCHED',        nextStatus: 'EN_ROUTE_PICKUP',   label: 'Start Trip',             icon: 'play' },
  { status: 'EN_ROUTE_PICKUP',   nextStatus: 'AT_PICKUP',         label: 'Arrived at Pickup',      icon: 'map-pin' },
  { status: 'AT_PICKUP',         nextStatus: 'LOADING',           label: 'Start Loading',          icon: 'package' },
  { status: 'LOADING',           nextStatus: 'EN_ROUTE_DELIVERY', label: 'Depart for Delivery',    icon: 'navigation' },
  { status: 'EN_ROUTE_DELIVERY', nextStatus: 'AT_DELIVERY',       label: 'Arrived at Delivery',    icon: 'map-pin' },
  { status: 'AT_DELIVERY',       nextStatus: 'UNLOADING',         label: 'Start Unloading',        icon: 'package' },
  { status: 'UNLOADING',         nextStatus: 'COMPLETED',         label: 'Complete Trip',          icon: 'check-circle' },
];

export function getNextAction(currentStatus: string): StatusActionDSL | undefined {
  return STATUS_ACTIONS.find(a => a.status === currentStatus);
}

// ─── Trip card DSL ───────────────────────────────────────────────────────────

export const TRIP_CARD_DSL: CardDSL = {
  titleField: 'tripNo',
  subtitleField: 'tripType',
  badgeField: 'tripStatus',
  badgeFormat: 'status',
  bodyFields: ['jobNo', 'customerName', 'vehicleNo'],
  timestampField: 'scheduledDate',
};

// ─── Exception types ─────────────────────────────────────────────────────────

export const EXCEPTION_TYPES = [
  { type: 'DELAY',              label: 'Delay',               description: 'Running behind schedule' },
  { type: 'DAMAGE',             label: 'Cargo Damage',        description: 'Goods damaged during transport' },
  { type: 'NO_ACCESS',          label: 'Access Issue',        description: 'Cannot reach delivery site' },
  { type: 'VEHICLE_BREAKDOWN',  label: 'Vehicle Breakdown',   description: 'Vehicle mechanical failure' },
  { type: 'DOCUMENTATION',      label: 'Document Problem',    description: 'Missing or wrong paperwork' },
  { type: 'OTHER',              label: 'Other Issue',         description: 'Describe in notes' },
] as const;

// ─── Stop status icons ──────────────────────────────────────────────────────

export const STOP_STATUS_STYLES: Record<string, { icon: string; color: string }> = {
  PENDING:   { icon: 'circle',        color: 'text-muted-foreground' },
  ARRIVED:   { icon: 'circle-dot',    color: 'text-amber-500' },
  COMPLETED: { icon: 'check-circle',  color: 'text-green-600' },
  SKIPPED:   { icon: 'square',        color: 'text-muted-foreground' },
};

// ─── Delivery at-delivery status check ───────────────────────────────────────

export function isAtDeliveryPhase(status: string): boolean {
  return status === 'AT_DELIVERY' || status === 'UNLOADING';
}
